import React, { useEffect } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { themeColor } from "../../../../types/color";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../../../utils/fontWeightOptions";
import { ChatHistoryWidgetState, ChatHistoryWidgetProps } from "./types";

const loadGoogleFontInHead = (fontUrl: string): void => {
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

// ---------------------------------------------------------------------------
// Default state
// ---------------------------------------------------------------------------

const defaultState: ChatHistoryWidgetState = {
  pageTitle: "Chat History",
  pageTitleFontSize: 24,
  pageTitleFontWeight: "semibold",
  pageTitleColor: themeColor("textPrimary"),

  searchPlaceholder: "Search your chats",
  searchBgColor: themeColor("background"),
  searchBorderColor: themeColor("borderColor"),
  searchTextColor: themeColor("textPrimary"),
  searchIconColor: themeColor("textSecondary"),
  searchBorderRadius: 100,

  chatItemBgColor: themeColor("background"),
  chatItemHoverBgColor: themeColor("secondary"),
  chatItemSelectedBgColor: themeColor("secondary"),
  chatItemTitleColor: themeColor("textPrimary"),
  chatItemPreviewColor: themeColor("textSecondary"),
  chatItemTimestampColor: themeColor("textPrimary"),
  chatItemTitleFontSize: 14,
  chatItemPreviewFontSize: 12,
  chatItemBorderRadius: 10,
  chatItemPaddingX: 24,
  chatItemPaddingY: 16,

  groupHeaderColor: themeColor("textSecondary"),
  groupHeaderFontSize: 14,
  groupHeaderFontWeight: "medium",

  selectionCheckboxColor: themeColor("primary"),
  bulkActionBarBgColor: themeColor("background"),
  bulkActionBarTextColor: themeColor("textPrimary"),
  deleteButtonColor: themeColor("error"),
  deleteButtonHoverColor: themeColor("error"),

  pageBgColor: themeColor("background"),

  newChatButtonBgColor: themeColor("primary"),
  newChatButtonTextColor: themeColor("buttonText"),
  newChatButtonBorderRadius: 100,

  fontFamily: "inherit",
};

// ---------------------------------------------------------------------------
// Edit fields
// ---------------------------------------------------------------------------

const FONT_WEIGHT_OPTIONS_SHORT = FONT_WEIGHT_OPTIONS_KEYWORDS.slice(0, 4);

const editFields: EditField[] = [
  // ── Page Header
  { key: "pageTitle", label: "Page Title", type: "text", group: "Header" },
  {
    key: "pageTitleFontSize",
    label: "Title Size",
    type: "number",
    min: 18,
    max: 40,
    step: 1,
    group: "Header",
  },
  {
    key: "pageTitleFontWeight",
    label: "Title Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_SHORT],
    group: "Header",
  },
  {
    key: "pageTitleColor",
    label: "Title Color",
    type: "color",
    group: "Header",
  },

  // ── Search Bar
  {
    key: "searchPlaceholder",
    label: "Placeholder",
    type: "text",
    group: "Search",
  },
  {
    key: "searchBgColor",
    label: "Background",
    type: "color",
    group: "Search",
  },
  {
    key: "searchBorderColor",
    label: "Border Color",
    type: "color",
    group: "Search",
  },
  {
    key: "searchTextColor",
    label: "Text Color",
    type: "color",
    group: "Search",
  },
  {
    key: "searchIconColor",
    label: "Icon Color",
    type: "color",
    group: "Search",
  },
  {
    key: "searchBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 24,
    step: 2,
    group: "Search",
  },

  // ── Chat Items
  {
    key: "chatItemBgColor",
    label: "Background",
    type: "color",
    group: "Chat Items",
  },
  {
    key: "chatItemHoverBgColor",
    label: "Hover Background",
    type: "color",
    group: "Chat Items",
  },
  {
    key: "chatItemSelectedBgColor",
    label: "Selected Background",
    type: "color",
    group: "Chat Items",
  },
  {
    key: "chatItemTitleColor",
    label: "Title Color",
    type: "color",
    group: "Chat Items",
  },
  {
    key: "chatItemPreviewColor",
    label: "Preview Color",
    type: "color",
    group: "Chat Items",
  },
  {
    key: "chatItemTimestampColor",
    label: "Timestamp Color",
    type: "color",
    group: "Chat Items",
  },
  {
    key: "chatItemTitleFontSize",
    label: "Title Size",
    type: "number",
    min: 12,
    max: 20,
    step: 1,
    group: "Chat Items",
  },
  {
    key: "chatItemPreviewFontSize",
    label: "Preview Size",
    type: "number",
    min: 10,
    max: 16,
    step: 1,
    group: "Chat Items",
  },
  {
    key: "chatItemBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 16,
    step: 2,
    group: "Chat Items",
  },
  {
    key: "chatItemPaddingX",
    label: "Padding X",
    type: "number",
    min: 8,
    max: 32,
    step: 2,
    group: "Chat Items",
  },
  {
    key: "chatItemPaddingY",
    label: "Padding Y",
    type: "number",
    min: 8,
    max: 24,
    step: 2,
    group: "Chat Items",
  },

  // ── Group Headers
  {
    key: "groupHeaderColor",
    label: "Color",
    type: "color",
    group: "Group Headers",
  },
  {
    key: "groupHeaderFontSize",
    label: "Font Size",
    type: "number",
    min: 10,
    max: 18,
    step: 1,
    group: "Group Headers",
  },
  {
    key: "groupHeaderFontWeight",
    label: "Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_SHORT],
    group: "Group Headers",
  },

  // ── Selection / Bulk Actions
  {
    key: "selectionCheckboxColor",
    label: "Checkbox Color",
    type: "color",
    group: "Selection",
  },
  {
    key: "bulkActionBarBgColor",
    label: "Action Bar BG",
    type: "color",
    group: "Selection",
  },
  {
    key: "bulkActionBarTextColor",
    label: "Action Bar Text",
    type: "color",
    group: "Selection",
  },
  {
    key: "deleteButtonColor",
    label: "Delete Button",
    type: "color",
    group: "Selection",
  },

  // ── Page Background
  { key: "pageBgColor", label: "Page BG", type: "color", group: "Layout" },

  // ── New Chat Button
  {
    key: "newChatButtonBgColor",
    label: "Background",
    type: "color",
    group: "New Chat Button",
  },
  {
    key: "newChatButtonTextColor",
    label: "Text Color",
    type: "color",
    group: "New Chat Button",
  },
  {
    key: "newChatButtonBorderRadius",
    label: "Border Radius",
    type: "number",
    min: 0,
    max: 24,
    step: 2,
    group: "New Chat Button",
  },

  // ── Typography
  {
    key: "fontFamily",
    label: "Font Family",
    type: "font",
    group: "Typography",
  },
];

// ---------------------------------------------------------------------------
// Widget component
// ---------------------------------------------------------------------------

export const ChatHistoryWidget: React.FC<ChatHistoryWidgetProps> = ({
  widgetId = "chat-history",
  displayName = "Chat History",
  initialState = {},
  wrapperStyle,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    themeIcons,
    resolveColor,
    canSave,
  } = useWidgetEditable<ChatHistoryWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields,
  );

  const fontKey = state.fontFamily;
  const resolvedFontData =
    fontKey && fontKey !== "inherit" ? themeFonts[fontKey] : null;
  const fallbackFontData = !resolvedFontData
    ? themeFonts["studio-brand-font"] ||
      themeFonts["body"] ||
      Object.values(themeFonts)[0] ||
      null
    : null;
  const fontData = resolvedFontData || fallbackFontData;

  const googleFontLink =
    fontData && fontData.format === "google" ? fontData.url : null;

  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  if (isLoading) {
    return <></>;
  }

  const fontFamilyCss = fontData
    ? fontData.format === "system"
      ? fontData.url
      : `"${fontData.name}", sans-serif`
    : "inherit";

  const needsFontFace =
    fontData && fontData.format !== "system" && fontData.format !== "google";

  const fontFaceCss = needsFontFace
    ? `@font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }`
    : "";

  const enhancedState: ChatHistoryWidgetState & {
    fontFamilyCss?: string;
  } = {
    ...state,
    fontFamilyCss,
  };

  const widgetContent = (
    <>
      {fontFaceCss && <style>{fontFaceCss}</style>}
      {children(enhancedState, resolveColor)}
    </>
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
      themeIcons={themeIcons}
      wrapperStyle={wrapperStyle}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
